/**
 * Iter 111 · Admin CPanel · Users & Kiosks tab.
 *
 * Ελληνικό UI. Λίστα περιπτέρων με φίλτρα κατάστασης, αναζήτηση,
 * badges & row actions (Activate / Suspend / Extend / Convert /
 * Notes / Details). Αυτόματο refresh κάθε 60 δευτ. (piggy-backs στο
 * Dashboard interval μέσω του parent AdminCPanel).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  CheckCircle2,
  XCircle,
  Plus,
  CreditCard,
  Lock,
  Unlock,
  StickyNote,
  Eye,
  RefreshCw,
  Trash2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function readJwt() {
  return (
    localStorage.getItem("kf_jwt") ||
    localStorage.getItem("kiosk_jwt") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

const FILTERS = [
  { key: "all",       label: "Όλα",          emoji: "📋" },
  { key: "online",    label: "Online",       emoji: "🟢" },
  { key: "trial",     label: "Trial",        emoji: "🎁" },
  { key: "active",    label: "Ενεργά",       emoji: "✅" },
  { key: "expired",   label: "Ληγμένα",      emoji: "❌" },
  { key: "locked",    label: "Κλειδωμένα",   emoji: "🔒" },
];

// Status badge palette
const STATUS_META = {
  trial:     { label: "Trial",     cls: "bg-sky-500/20 text-sky-300 ring-sky-400/40" },
  active:    { label: "Ενεργό",    cls: "bg-emerald-500/20 text-emerald-300 ring-emerald-400/40" },
  expired:   { label: "Ληγμένο",   cls: "bg-rose-500/20 text-rose-300 ring-rose-400/40" },
  suspended: { label: "Απενεργό",  cls: "bg-slate-500/20 text-slate-300 ring-slate-400/40" },
  locked:    { label: "Κλειδωμένο", cls: "bg-slate-500/20 text-slate-300 ring-slate-400/40" },
  unknown:   { label: "—",         cls: "bg-slate-500/20 text-slate-300 ring-slate-400/40" },
};

function StatusBadge({ status, online }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <div className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ${meta.cls}`}
        data-testid={`kiosk-status-${status}`}
      >
        {meta.label}
      </span>
      {online && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-200 ring-1 ring-emerald-400/40"
          data-testid="kiosk-online-dot"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgb(52,211,153)]" />
          Online
        </span>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("el-GR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("el-GR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ── Generic Modal shell ────────────────────────────────────────────────────
function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="kiosk-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${width} rounded-2xl border border-white/10 bg-slate-900 shadow-2xl`}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <h3 className="text-base font-black text-white">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-white"
            data-testid="kiosk-modal-close"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/40";

// ── Activate / Convert modal ───────────────────────────────────────────────
function ActivateModal({ open, onClose, kiosk, onSubmit, convertMode }) {
  const [amount, setAmount] = useState("99.90");
  const [days, setDays] = useState("30");
  const [method, setMethod] = useState("bank");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setAmount("99.90"); setDays("30"); setMethod("bank"); setNote("");
    }
  }, [open]);
  async function submit() {
    setBusy(true);
    try {
      await onSubmit({
        amount: parseFloat(amount) || 0,
        duration_days: parseInt(days, 10) || 30,
        method,
        note,
      });
      onClose();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={convertMode ? "💳 Trial → Πληρωμένο" : "✅ Ενεργοποίηση Περιπτέρου"}
    >
      <div className="mb-3 rounded-lg bg-white/5 px-3 py-2 text-xs text-slate-300">
        <div className="font-bold text-white">{kiosk?.company_name}</div>
        <div>{kiosk?.owner_email}</div>
      </div>
      <div className="space-y-3">
        <Field label="Ποσό (€)">
          <input
            type="number" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputCls}
            data-testid="activate-amount"
          />
        </Field>
        <Field label="Διάρκεια (ημέρες)">
          <input
            type="number" value={days}
            onChange={(e) => setDays(e.target.value)}
            className={inputCls}
            data-testid="activate-days"
          />
        </Field>
        <Field label="Μέθοδος">
          <div className="grid grid-cols-4 gap-2" data-testid="activate-method-group">
            {[
              { v: "bank",   l: "Τραπεζική" },
              { v: "cash",   l: "Μετρητά" },
              { v: "paypal", l: "PayPal" },
              { v: "free",   l: "Δωρεάν" },
            ].map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setMethod(m.v)}
                data-testid={`activate-method-${m.v}`}
                className={`rounded-lg border px-2 py-2 text-xs font-bold transition ${
                  method === m.v
                    ? "border-emerald-400 bg-emerald-500/20 text-emerald-100"
                    : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {m.l}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Σημείωση (προαιρετικό)">
          <textarea
            rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls}
            data-testid="activate-note"
          />
        </Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button" onClick={onClose}
          className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5"
        >
          Ακύρωση
        </button>
        <button
          type="button" onClick={submit} disabled={busy}
          data-testid="activate-submit"
          className="rounded-lg bg-gradient-to-br from-emerald-500 to-lime-500 px-4 py-2 text-xs font-black text-slate-950 shadow-lg transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "…" : convertMode ? "Μετατροπή" : "Ενεργοποίηση"}
        </button>
      </div>
    </Modal>
  );
}

// ── Extend trial modal ─────────────────────────────────────────────────────
function ExtendModal({ open, onClose, kiosk, onSubmit }) {
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setDays("7"); }, [open]);
  async function submit() {
    setBusy(true);
    try {
      await onSubmit({ days: parseInt(days, 10) || 7 });
      onClose();
    } finally { setBusy(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="➕ Παράταση Trial">
      <div className="mb-3 rounded-lg bg-white/5 px-3 py-2 text-xs text-slate-300">
        <div className="font-bold text-white">{kiosk?.company_name}</div>
        <div>Τρέχουσα λήξη · {formatDate(kiosk?.expiry_date)}</div>
      </div>
      <Field label="Πρόσθεσε ημέρες">
        <input
          type="number" value={days}
          onChange={(e) => setDays(e.target.value)}
          className={inputCls}
          data-testid="extend-days"
        />
      </Field>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button" onClick={onClose}
          className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5"
        >
          Ακύρωση
        </button>
        <button
          type="button" onClick={submit} disabled={busy}
          data-testid="extend-submit"
          className="rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 px-4 py-2 text-xs font-black text-slate-950 shadow-lg transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "…" : "Παράταση"}
        </button>
      </div>
    </Modal>
  );
}

// ── Notes modal ────────────────────────────────────────────────────────────
function NotesModal({ open, onClose, kiosk, onSubmit }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(kiosk?.notes || ""); }, [open, kiosk]);
  async function submit() {
    setBusy(true);
    try { await onSubmit({ note }); onClose(); }
    finally { setBusy(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="📝 Σημείωση Admin">
      <Field label={`Σημείωση για ${kiosk?.company_name || "…"}`}>
        <textarea
          rows={4} value={note}
          onChange={(e) => setNote(e.target.value)}
          className={inputCls}
          data-testid="notes-textarea"
        />
      </Field>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button" onClick={onClose}
          className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5"
        >
          Ακύρωση
        </button>
        <button
          type="button" onClick={submit} disabled={busy}
          data-testid="notes-submit"
          className="rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 px-4 py-2 text-xs font-black text-white shadow-lg transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "…" : "Αποθήκευση"}
        </button>
      </div>
    </Modal>
  );
}

// ── Details modal ──────────────────────────────────────────────────────────
function DetailsModal({ open, onClose, kiosk, detail }) {
  return (
    <Modal open={open} onClose={onClose} title="👁️ Λεπτομέρειες Περιπτέρου" size="lg">
      {kiosk && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Row label="Όνομα" value={kiosk.company_name} />
          <Row label="Tenant ID" value={<code className="text-emerald-300">{kiosk.tenant_id}</code>} />
          <Row label="Email" value={kiosk.owner_email} />
          <Row label="Τηλέφωνο" value={kiosk.phone} />
          <Row label="Κατάσταση" value={<StatusBadge status={kiosk.status} online={kiosk.online} />} />
          <Row label="Πλάνο" value={kiosk.subscription_plan} />
          <Row label="Λήξη" value={formatDateTime(kiosk.expiry_date)} />
          <Row label="Τελευταία σύνδεση" value={formatDateTime(kiosk.last_seen)} />
          <Row label="IP" value={kiosk.last_ip} />
          <Row label="Δημιουργία" value={formatDateTime(kiosk.created_at)} />
          <div className="col-span-2">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">Σημείωση</span>
            <div className="rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm text-slate-200 min-h-[3rem]">
              {kiosk.notes || <em className="text-slate-500">—</em>}
            </div>
          </div>
          {detail?.owner && (
            <div className="col-span-2 mt-2">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-slate-400">Owner</span>
              <div className="rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-xs text-slate-300">
                <div>{detail.owner.email}</div>
                <div>role · {detail.owner.role} · created {formatDateTime(detail.owner.created_at)}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-sm text-white">{value ?? "—"}</div>
    </div>
  );
}

// ── Main tab ───────────────────────────────────────────────────────────────
export default function AdminUsersKiosks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openActivate, setOpenActivate] = useState({ open: false, mode: "activate", kiosk: null });
  const [openExtend, setOpenExtend] = useState({ open: false, kiosk: null });
  const [openNotes, setOpenNotes] = useState({ open: false, kiosk: null });
  const [openDetails, setOpenDetails] = useState({ open: false, kiosk: null, detail: null });

  const client = useMemo(
    () => axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: { Authorization: `Bearer ${readJwt()}` },
      timeout: 15_000,
    }),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/kiosks", {
        params: { status: filter, q: q || undefined },
      });
      setRows(data.kiosks || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία φόρτωσης περιπτέρων");
    } finally {
      setLoading(false);
    }
  }, [client, filter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function fireAction(kiosk, path, payload, msg) {
    try {
      const { data } = await client.post(`/admin/kiosks/${kiosk.tenant_id}${path}`, payload || {});
      toast.success(msg || "OK");
      // Refresh from server so all rows sync (status filter may drop the row).
      await load();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία ενέργειας");
      throw err;
    }
  }

  return (
    <div className="space-y-5" data-testid="cpanel-users-tab">
      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-slate-900/70 px-3 py-2 backdrop-blur">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Αναζήτηση · όνομα · email · τηλέφωνο"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
            data-testid="kiosks-search"
          />
        </div>
        <button
          type="button" onClick={load}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10"
          data-testid="kiosks-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="kiosks-filter-group">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`kiosks-filter-${f.key}`}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
              filter === f.key
                ? "bg-emerald-500 text-slate-950 shadow-lg"
                : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
            }`}
          >
            <span className="mr-1">{f.emoji}</span>{f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div
        className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur"
        data-testid="kiosks-table-wrap"
      >
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="kiosks-loading">
            Φόρτωση…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="kiosks-empty">
            <div className="text-base font-semibold text-white">Δεν βρέθηκαν καταχωρήσεις</div>
            <div className="text-xs text-slate-400 mt-1">
              No kiosks match the current filter · Δοκιμάστε άλλο φίλτρο.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kiosks-table">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3">Όνομα · Tenant</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Τηλέφωνο</th>
                  <th className="text-left px-4 py-3">Κατάσταση</th>
                  <th className="text-left px-4 py-3">Λήξη</th>
                  <th className="text-left px-4 py-3">Τελευταία σύνδεση</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-right px-4 py-3">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr
                    key={k.tenant_id}
                    data-testid={`kiosk-row-${k.tenant_id}`}
                    className="border-t border-white/5 transition hover:bg-white/[0.03]"
                  >
                    <td className="px-4 py-3">
                      <div className="font-bold text-white">{k.company_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{k.tenant_id}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-200">{k.owner_email}</td>
                    <td className="px-4 py-3 text-slate-300">{k.phone}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={k.status} online={k.online} />
                    </td>
                    <td className="px-4 py-3 text-slate-300">{formatDate(k.expiry_date)}</td>
                    <td className="px-4 py-3 text-slate-300">{formatDateTime(k.last_seen)}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{k.last_ip}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        <RowBtn
                          icon={CheckCircle2} tone="emerald" label="Ενεργοποίηση"
                          testid={`kiosk-activate-${k.tenant_id}`}
                          onClick={() => setOpenActivate({ open: true, mode: "activate", kiosk: k })}
                        />
                        {k.status === "suspended" || k.status === "locked" ? (
                          <RowBtn
                            icon={Unlock} tone="lime" label="Ξεκλείδωμα"
                            testid={`kiosk-unsuspend-${k.tenant_id}`}
                            onClick={() =>
                              fireAction(k, "/unsuspend", null, "Το περίπτερο ξεκλειδώθηκε.")
                            }
                          />
                        ) : (
                          <RowBtn
                            icon={Lock} tone="slate" label="Κλείδωμα"
                            testid={`kiosk-suspend-${k.tenant_id}`}
                            onClick={() =>
                              fireAction(k, "/suspend", null, "Το περίπτερο κλειδώθηκε.")
                            }
                          />
                        )}
                        <RowBtn
                          icon={Plus} tone="sky" label="Παράταση Trial"
                          testid={`kiosk-extend-${k.tenant_id}`}
                          onClick={() => setOpenExtend({ open: true, kiosk: k })}
                        />
                        <RowBtn
                          icon={CreditCard} tone="fuchsia" label="Trial → Paid"
                          testid={`kiosk-convert-${k.tenant_id}`}
                          onClick={() => setOpenActivate({ open: true, mode: "convert", kiosk: k })}
                        />
                        <RowBtn
                          icon={StickyNote} tone="amber" label="Σημείωση"
                          testid={`kiosk-notes-${k.tenant_id}`}
                          onClick={() => setOpenNotes({ open: true, kiosk: k })}
                        />
                        <RowBtn
                          icon={Eye} tone="indigo" label="Λεπτομέρειες"
                          testid={`kiosk-details-${k.tenant_id}`}
                          onClick={async () => {
                            try {
                              const { data } = await client.get(`/admin/kiosks/${k.tenant_id}`);
                              setOpenDetails({ open: true, kiosk: k, detail: data });
                            } catch (err) {
                              toast.error("Δεν φορτώθηκαν οι λεπτομέρειες.");
                            }
                          }}
                        />
                        {/* Iter 114 · Deep cascading delete of a kiosk */}
                        <RowBtn
                          icon={Trash2} tone="rose" label="Διαγραφή"
                          testid={`kiosk-delete-${k.tenant_id}`}
                          onClick={async () => {
                            if (k.tenant_id === "MASTER_JOHN") {
                              toast.error("Δεν επιτρέπεται η διαγραφή του MASTER tenant.");
                              return;
                            }
                            const ok = window.confirm(
                              `⚠️ ΠΡΟΣΟΧΗ! Θα διαγραφεί οριστικά το κατάστημα "${k.company_name || k.tenant_id}" ΚΑΙ ΟΛΑ τα δεδομένα του (invoices, cheques, photos, shifts, kronos, users). Η ενέργεια είναι ΜΗ αναστρέψιμη. Είσαι σίγουρος;`,
                            );
                            if (!ok) return;
                            try {
                              const { data } = await client.delete(`/admin/kiosks/${k.tenant_id}`);
                              toast.success(
                                `Διαγράφηκε · ${data.total_deleted} rows σε ${Object.keys(data.counts).length} collections.`,
                              );
                              await load();
                            } catch (err) {
                              toast.error(
                                err?.response?.data?.detail || "Αποτυχία διαγραφής",
                              );
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActivateModal
        open={openActivate.open}
        convertMode={openActivate.mode === "convert"}
        kiosk={openActivate.kiosk}
        onClose={() => setOpenActivate({ open: false, mode: "activate", kiosk: null })}
        onSubmit={(payload) =>
          fireAction(
            openActivate.kiosk,
            openActivate.mode === "convert" ? "/convert-to-paid" : "/activate",
            payload,
            openActivate.mode === "convert"
              ? "Trial μετατράπηκε σε πληρωμένο."
              : "Το περίπτερο ενεργοποιήθηκε.",
          )
        }
      />
      <ExtendModal
        open={openExtend.open}
        kiosk={openExtend.kiosk}
        onClose={() => setOpenExtend({ open: false, kiosk: null })}
        onSubmit={(payload) =>
          fireAction(openExtend.kiosk, "/extend-trial", payload, "Το trial παρατάθηκε.")
        }
      />
      <NotesModal
        open={openNotes.open}
        kiosk={openNotes.kiosk}
        onClose={() => setOpenNotes({ open: false, kiosk: null })}
        onSubmit={(payload) =>
          fireAction(openNotes.kiosk, "/notes", payload, "Η σημείωση αποθηκεύτηκε.")
        }
      />
      <DetailsModal
        open={openDetails.open}
        kiosk={openDetails.kiosk}
        detail={openDetails.detail}
        onClose={() => setOpenDetails({ open: false, kiosk: null, detail: null })}
      />
    </div>
  );
}

const TONE_CLS = {
  emerald:  "border-emerald-400/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25",
  rose:     "border-rose-400/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25",
  slate:    "border-slate-400/30 bg-slate-500/15 text-slate-200 hover:bg-slate-500/25",
  lime:     "border-lime-400/40 bg-lime-500/15 text-lime-200 hover:bg-lime-500/25",
  sky:      "border-sky-400/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25",
  fuchsia:  "border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25",
  amber:    "border-amber-400/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25",
  indigo:   "border-indigo-400/40 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25",
};

function RowBtn({ icon: Icon, tone, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition ${TONE_CLS[tone] || TONE_CLS.slate}`}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}
